import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('auth')

import { defineStore } from 'pinia'
import { ApiService, ApiRoutes } from '@/services/api.service'
import { StorageService } from '@/services/storage.service'
import type {
  ILoginRequest,
  AuthState,
  User,
  IRegisterRequest,
} from '@/types'
import { IApiError } from '@/types'
import { ServiceContainer } from '@/services/service-container'
import { getConfig } from '@/config'
import { BUILD_FEATURES } from '@/config/build-features'
import { profileImageUrlFromApiValue } from '@/utils/profileImage'

/** localStorage keys that belong to the device, not the logged-in account */
const DEVICE_LOCAL_STORAGE_KEYS = ['theme', 'user_locale_preference'] as const

function preserveDeviceLocalStoragePrefs(): Record<string, string> {
  const preserved: Record<string, string> = {}
  for (const key of DEVICE_LOCAL_STORAGE_KEYS) {
    const value = localStorage.getItem(key)
    if (value != null) preserved[key] = value
  }
  return preserved
}

function restoreDeviceLocalStoragePrefs(preserved: Record<string, string>): void {
  for (const [key, value] of Object.entries(preserved)) {
    localStorage.setItem(key, value)
  }
}

/**
 * Auth store
 * @returns Auth store
 */
export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    token: null,
    initializing: true
  }),

  getters: {
    isAuthenticated: (state) => {
      // First check state token
      if (state.token) return true
      
      // Then check storage token - use the prefix from config
      // Important: The storage service stores values as JSON strings,
      // so the raw localStorage value will have quotes around it
      const prefix = `${getConfig().app.id}_`
      const rawToken = localStorage.getItem(`${prefix}auth_token`)
      
      // The token in localStorage is stored as a JSON string (with quotes),
      // so we need to check if it exists and is not null/"null"
      return !!rawToken && rawToken !== 'null' && rawToken !== '"null"'
    }
  },

  actions: {
    /**
     * Resolve persistent web device id for Sanctum token naming.
     */
    async resolveDeviceId(): Promise<string | undefined> {
      try {
        const services = ServiceContainer.getInstance()
        const device = await services.get('device')
        return await device.getDeviceId()
      } catch (error) {
        fileLogger.warn('Could not resolve deviceId for auth request:', error)
        return undefined
      }
    },

    /**
     * Login
     * @param credentials - Login credentials
     * @param isPopupLogin - Flag indicating if this login is from a popup (default: false)
     */
    async login(credentials: ILoginRequest, isPopupLogin = false): Promise<void> {
      const hadExistingSession = Boolean(this.token)
      try {
        const services = ServiceContainer.getInstance()
        const api = await services.get<ApiService>('api')

        const config = getConfig()
        const deviceId = await this.resolveDeviceId()

        const loginData = {
          ...credentials,
          app_id: config.app.contentId,
          ...(deviceId ? { deviceId } : {}),
        }

        const response = await api.auth(ApiRoutes.Login, loginData)

        if (!response.access_token) {
          throw new Error('Invalid response from server: Missing access token')
        }

        await this.setAuthToken(response.access_token, response.refresh_token)
        await this.getUser()

        try {
          const trackingService = await services.get('tracking')
          await trackingService.trackLogin()
        } catch (error) {
          fileLogger.error('Error tracking login event:', error)
        }

        try {
          const { pushEvent } = await import('@/lib/analytics')
          pushEvent('login', { method: 'email' })
        } catch (error) {
          fileLogger.error('Error pushing login analytics event:', error)
        }
      } catch (error) {
        // Failed credentials must not call logout() — that resets library/player
        // and localStorage.clear(), wiping guest Continue Listening progress.
        fileLogger.log(
          isPopupLogin
            ? 'Popup login failed, not clearing auth state'
            : 'Login failed, leaving local library state intact',
        )
        await this.rollbackFailedAuthAttempt(hadExistingSession)
        throw error
      }
    },

    /**
     * Register
     * @param data - Register data
     */
    async register(data: IRegisterRequest): Promise<void> {
      const hadExistingSession = Boolean(this.token)
      try {
        const services = ServiceContainer.getInstance()
        const api = await services.get<ApiService>('api')

        const config = getConfig()
        const deviceId = await this.resolveDeviceId()

        const registerData = {
          ...data,
          app_id: config.app.contentId,
          ...(deviceId ? { deviceId } : {}),
        }

        const response = await api.auth(ApiRoutes.Register, registerData)
        await this.setAuthToken(response.access_token, response.refresh_token)
        await this.getUser()

        try {
          const trackingService = await services.get('tracking')
          await trackingService.trackRegistration()
        } catch (error) {
          fileLogger.error('Error tracking registration event:', error)
        }

        try {
          const { pushEvent } = await import('@/lib/analytics')
          pushEvent('sign_up', { method: 'email' })
        } catch (error) {
          fileLogger.error('Error pushing sign_up analytics event:', error)
        }
      } catch (error) {
        fileLogger.log('Registration failed, leaving local library state intact')
        await this.rollbackFailedAuthAttempt(hadExistingSession)
        throw error
      }
    },

    /**
     * Drop a token written by a failed login/register without wiping library data.
     * Existing sessions are left untouched so a mistyped password cannot log the user out.
     */
    async rollbackFailedAuthAttempt(hadExistingSession: boolean): Promise<void> {
      if (hadExistingSession) {
        return
      }

      this.token = null
      try {
        const { http } = await import('@/utils/http')
        await http.setToken(null)
      } catch (error) {
        fileLogger.warn('Could not roll back auth token after failed auth:', error)
      }
    },

    /**
     * Set auth token (and optional refresh token from login/register/refresh).
     * @param token - Access token
     * @param refreshToken - Opaque refresh token (rotated by API when present)
     */
    async setAuthToken(token: string, refreshToken?: string | null): Promise<void> {
      fileLogger.log('Setting auth token')
      // Apply JWT to axios/storage BEFORE flipping state.token. Watchers on
      // isAuthenticated (e.g. Detail title reload) build non-guest API URLs and
      // must send Authorization on the same tick the store becomes authenticated.
      const { http } = await import('@/utils/http')
      await http.setToken(token)
      if (refreshToken) {
        await http.setRefreshToken(refreshToken)
      }
      this.token = token
      fileLogger.log('Auth token stored successfully')
    },

    /**
     * Request password reset
     * @param email - Email
     */
    async requestPasswordReset(email: string): Promise<void> {
      const services = ServiceContainer.getInstance()
      const api = await services.get<ApiService>('api')
      
      // Ensure the base URL doesn't have a trailing slash
      const baseUrl = getConfig().api.appUrl.replace(/\/+$/, '')
      
      await api.post(ApiRoutes.ForgotPassword, { 
        email,
        redirect_url: `${baseUrl}/reset-password`
      })
    },

    /**
     * Validate reset code
     * @param email - Email
     * @param code - 6-digit verification code
     * @returns reset_code - The validated reset code to use for password reset
     */
    async validateResetCode(email: string, code: string): Promise<string> {
      const services = ServiceContainer.getInstance()
      const api = await services.get<ApiService>('api')
      
      try {
        console.log(`Validating reset code for ${email}`)
        
        // Make the API request
        const response = await api.post(ApiRoutes.ValidateResetCode, {
          email,
          reset_code: code
        })
        
        console.log('Raw validation response:', response)
        
        // Handle the specific response format from your API
        let isValid = false
        
        // Check different possible successful response structures:
        if (
          // Direct boolean response
          response === true ||
          // Object with valid property
          (response && typeof response === 'object' && (response as any).valid === true) ||
          // Object with data.valid property
          (response && typeof response === 'object' && (response as any).data && 
           typeof (response as any).data === 'object' && (response as any).data.valid === true) ||
          // Special case: empty object in data can also mean success
          (response && typeof response === 'object' && (response as any).data && 
           typeof (response as any).data === 'object')
        ) {
          isValid = true
        }
        
        console.log('Is validation successful?', isValid)
        
        if (isValid) {
          return code
        } else {
          throw new Error('Invalid verification code')
        }
      } catch (error) {
        // Check if it's already an ApiError instance created by the HTTP service
        console.error('Validation error:', error)
        
        if (error instanceof Error) {
          // Try to detect validation errors
          const apiError = error as any
          if (apiError.status === 422 || 
              apiError.response?.status === 422 ||
              error.message.includes('422')) {
            throw new Error('Invalid verification code. Please check the code and try again.')
          }
          
          // If there's a specific error message, use it
          throw error
        }
        
        // Fallback error
        throw new Error('Failed to verify code. Please try again.')
      }
    },

    /**
     * Reset password
     * @param token - Reset token (optional if reset_code is provided)
     * @param password - Password
     * @param email - Email
     * @param password_confirmation - Password confirmation (optional, defaults to same as password)
     * @param reset_code - 6-digit verification code (optional if token is provided)
     */
    async resetPassword(token: string | undefined, password: string, email: string, password_confirmation?: string, reset_code?: string): Promise<void> {
      const services = ServiceContainer.getInstance()
      const api = await services.get<ApiService>('api')
      
      // Ensure either token or reset_code is present
      if (!token && !reset_code) {
        throw new Error('Either token or reset_code must be provided')
      }
      
      // Create payload with the provided credentials
      const payload: Record<string, any> = { 
        password, 
        email, 
        password_confirmation: password_confirmation || password
      }
      
      // Add either token or reset_code to the payload
      if (token) {
        payload.token = token
      }
      
      if (reset_code) {
        payload.reset_code = reset_code
      }
      
      try {
        await api.post(ApiRoutes.ResetPassword, payload)
      } catch (error) {
        console.error('Password reset failed:', error)
        throw error
      }
    },

    /**
     * Logout
     * @param options.skipSync - Skip library sync before logout
     * @param options.reason - `session_invalid` = soft clear (keep device_id / local library);
     *   omit or `user` = explicit logout with full storage wipe
     */
    async logout(options?: {
      skipSync?: boolean
      reason?: 'session_invalid' | 'user'
    }): Promise<void> {
      const isSoftInvalidation = options?.reason === 'session_invalid'

      try {
        const services = ServiceContainer.getInstance()
        const storage = await services.get<StorageService>('storage')

        const { useLibraryStore } = await import('@/stores/library')
        const { usePlayerStore } = await import('@/stores/player')
        const { useLayoutStore } = await import('@/stores/layout')
        const { useSettingsStore } = await import('@/stores/settings')
        const { useSearchStore } = await import('@/stores/search')
        const libraryStore = useLibraryStore()
        const playerStore = usePlayerStore()
        const layoutStore = useLayoutStore()
        const settingsStore = useSettingsStore()
        const searchStore = useSearchStore()

        if (!isSoftInvalidation && !options?.skipSync && this.isAuthenticated) {
          fileLogger.log('Syncing data with backend before logout')
          try {
            await libraryStore.syncLibrary()
            fileLogger.log('Data successfully synced before logout')
          } catch (error) {
            fileLogger.error('Failed to sync data before logout:', error)
          }
        }

        this.token = null
        this.user = null

        const { http } = await import('@/utils/http')
        await http.setToken(null)

        if (isSoftInvalidation) {
          // Keep device_id, library cache, and guest progress — only drop auth tokens
          fileLogger.log('Soft session invalidation: auth cleared, local data preserved')
          return
        }

        libraryStore.reset()
        playerStore.reset()
        layoutStore.reset()
        settingsStore.resetSettings()
        searchStore.resetSearch()
        if (BUILD_FEATURES.epubReader) {
          const { useEpubStore } = await import('@/stores/epub')
          useEpubStore().reset()
        }

        await storage.clear()

        try {
          // Device-level prefs must survive account switch (esp. apps with defaultMode light)
          const preserved = preserveDeviceLocalStoragePrefs()
          try {
            const { useThemeStore } = await import('@/stores/theme')
            const themeStore = useThemeStore()
            if (themeStore.theme === 'light' || themeStore.theme === 'dark') {
              preserved.theme = themeStore.theme
            }
          } catch (error) {
            fileLogger.error('Error reading theme store before logout clear:', error)
          }
          localStorage.clear()
          restoreDeviceLocalStoragePrefs(preserved)
          try {
            const { useThemeStore } = await import('@/stores/theme')
            useThemeStore().init()
          } catch (error) {
            fileLogger.error('Error re-applying theme after logout:', error)
          }
          fileLogger.log('All localStorage cleared (device prefs preserved)')
        } catch (error) {
          fileLogger.error('Error clearing localStorage:', error)
        }

        fileLogger.log('User logged out, all storage cleared and store states reset')
      } catch (error) {
        fileLogger.error('Error during logout:', error)
      }
    },

    /**
     * Get user and set auth state
     */
    async getUser(): Promise<void> {
      fileLogger.log('Getting user data')
      const services = ServiceContainer.getInstance()
      const api = await services.get<ApiService>('api')
      const storage = await services.get<StorageService>('storage')
      const token = await storage.get('auth_token')
      
      if (!token) {
        fileLogger.log('No auth token found, cannot get user')
        return
      }

      try {
        fileLogger.log('Fetching user data from API')
        const response = await api.get<{data: User}>(ApiRoutes.Me)
        this.token = token as string
        
        // Store previous subscription status to detect changes
        const wasSubscribed = this.user?.subscribed
        
        // Update user data
        this.user = response.data

        if (this.user && this.user.profileImage != null) {
          this.user.profileImage = profileImageUrlFromApiValue(this.user.profileImage)
        }

        // Log subscription status change
        if (wasSubscribed !== this.user.subscribed) {
          fileLogger.log(`[AuthStore] User subscription status changed: ${wasSubscribed} → ${this.user.subscribed}`)
        }
        
        fileLogger.log('User data updated successfully')
      } catch (error) {
        fileLogger.error('Failed to get user:', error)

        // Only clear the session when the token is explicitly rejected.
        // Other failures (network, 5xx, parse/transform) must not wipe auth — that caused
        // "looks logged in until reload, then logged out" after subscription checkout.
        const isUnauthorized = error instanceof IApiError && error.status === 401

        if (isUnauthorized) {
          // Soft clear — interceptor may already have tried refresh; avoid nuclear wipe
          await this.logout({ skipSync: true, reason: 'session_invalid' })
        } else {
          fileLogger.warn('Error fetching user; keeping existing session')
          this.token = token as string
        }
      }
    },

    /**
     * Update user
     * @param data - User data 
     */
    async updateUser(data: User): Promise<void> {
      fileLogger.log('Updating user:', data)
      const services = ServiceContainer.getInstance()
      const api = await services.get<ApiService>('api')
      
      // Handle subscription status update specifically
      if (data.subscribed !== undefined && this.user && data.subscribed !== this.user.subscribed) {
        fileLogger.log(`[AuthStore] Updating subscription status: ${this.user.subscribed} → ${data.subscribed}`)

        // Replace user object so Pinia/Vue consumers re-render immediately
        this.user = { ...this.user, subscribed: data.subscribed }

        const storage = await services.get<StorageService>('storage')
        const storedUser = await storage.get<User>('user')
        if (storedUser) {
          await storage.set('user', { ...storedUser, subscribed: data.subscribed })
        }

        // No need to send to API as this should be handled by the subscription service
        return
      }
      
      // Handle password change specifically
      if (data.currentPassword && data.newPassword) {
        const passwordPayload = {
          data: {
            type: 'profile',
            attributes: {
              current_password: data.currentPassword,
              password: data.newPassword,
              password_confirmation: data.confirmPassword
            }
          }
        }
        await api.patch(ApiRoutes.UpdatePassword, passwordPayload)
        return
      }
      
      // Handle profile update
      // Only include fields that are actually provided
      const attributes: Record<string, any> = {}
      
      if (data.name !== undefined) attributes.name = data.name
      if (data.email !== undefined) attributes.email = data.email
      if (data.dateOfBirth !== undefined) attributes.date_of_birth = data.dateOfBirth
      if (data.profileImage !== undefined) attributes.profile_image = data.profileImage
      if (data.settings !== undefined) attributes.config = data.settings
      
      const payload = {
        data: {
          type: 'profile',
          attributes
        }
      }
      await api.patch(ApiRoutes.Me, payload)
      
      // Update the local user state to reflect the changes
      if (this.user) {
        // Update only the fields that were sent in the payload
        if (data.name !== undefined) this.user.name = data.name
        if (data.email !== undefined) this.user.email = data.email
        if (data.dateOfBirth !== undefined) this.user.dateOfBirth = data.dateOfBirth
        if (data.profileImage !== undefined) this.user.profileImage = data.profileImage
        if (data.settings !== undefined) this.user.settings = data.settings
        if (data.subscribed !== undefined) this.user.subscribed = data.subscribed
      }
    },

    //deactivate user
    async deactivateUser(): Promise<{
      deletion_scheduled_at?: string | null
      grace_period_days?: number
    }> {
      const services = ServiceContainer.getInstance()
      const api = await services.get<ApiService>('api')
      return api.post(ApiRoutes.Deactivate)
    },

    /**
     * Initialize auth
     */
    async initializeAuth() {
      try {
        fileLogger.log('Initializing auth')
        // Mark as initializing
        this.initializing = true
        
        const services = ServiceContainer.getInstance()
        const storage = await services.get<StorageService>('storage')
        
        // Get token using storage service which handles prefixing properly
        const token = await storage.get('auth_token')
        
        // Log raw token value for debugging
        const prefix = `${getConfig().app.id}_`
        const rawToken = localStorage.getItem(`${prefix}auth_token`)
        fileLogger.log('Raw token in localStorage:', 
                   rawToken ? `Found (${typeof rawToken}, length: ${rawToken.length})` : 'Not found')
        
        if (token) {
          fileLogger.log('Found existing auth token from storage service')
          this.token = token as string
          
          // Set the token in the HTTP service - do this in parallel with getUser
          const { http } = await import('@/utils/http')
          const tokenPromise = http.setToken(token as string)
          const userPromise = this.getUser()
          
          // Wait for both operations to complete in parallel
          await Promise.all([tokenPromise, userPromise])
          fileLogger.log('Parallel auth initialization complete')
        } else {
          fileLogger.log('No existing auth token found')
        }
        
        return true
      } catch (error) {
        fileLogger.error('Error initializing auth:', error)
        return false
      } finally {
        this.initializing = false
        fileLogger.log('Auth initialization complete')
      }
    },

    // Replace polling with Promise-based approach
    waitForInitialization() {
      if (!this.initializing) return Promise.resolve()
      
      return new Promise<void>(resolve => {
        const checkInitialized = () => {
          if (!this.initializing) {
            resolve()
          } else {
            // Check again in 50ms (faster than previous 100ms)
            setTimeout(checkInitialized, 50)
          }
        }
        checkInitialized()
      })
    }
  }
})
